<template>
  <wizard-base class="selling-strategy" :title="$t('WIZARD.SELLING_STRATEGY.TITLE')">
    <AssessmentValueBase :range-price-index="rangePriceIndex" :range-price="rangePrice">
      <template #action>
        <div class="selling-strategy__footer" @click="skipRangeInputCalculation = false">
          <p class="selling-strategy__label selling-strategy__label--left">
            {{ $t('WIZARD.SELLING_STRATEGY.CHEAP_OFFER') }}
          </p>
          <HdRange
            v-model="rangeInputPrice"
            name="selling-strategy"
            display-tooltip
            :tooltip-value="pricePercentageDiff"
            :min="min"
            :max="max"
            :labels="rangeLabels"
            :step="0.5"
            :step-bullets="customBullets"
            progress-background="transparent"
            track-background="radial-gradient(circle at center, #4CBA38, #FFE713, #E00016)"
            class="selling-strategy__range"
            @input="updatePriceFields"
          />
          <p class="selling-strategy__label">
            {{ $t('WIZARD.SELLING_STRATEGY.EXPENSIVE_OFFER') }}
          </p>
        </div>
      </template>
      <template #footer>
        <div v-show="shouldShowPriceReductionHint" class="selling-strategy__price-reduction-hint">
          <HdIcon :src="icons.info" class="price-reduction-hint__icon" width="25px" height="25px" />
          <p class="price-reduction-hint__message" v-html="priceReductionHint" />
        </div>
      </template>
    </AssessmentValueBase>
  </wizard-base>
</template>

<script>
// Components
import { HdRange, HdIcon } from 'homeday-blocks';
import WizardBase from '@/layout/WizardBase.vue';
import AssessmentValueBase from '@/views/wizard/AssessmentValueBase.vue';

// Services
import CONFIG from '@/config/env';

// Icons
import { documentsHandover } from 'homeday-assets/L';
import { info } from 'homeday-assets';

// Constants
import { GETTER, MUTATION } from '@/store/CONSTANTS';
import { DEFAULT_OPTIMAL_PRICE_VALUE, OPTIMAL_PRICE_FACTORS as FACTORS } from '@/config/CONSTANTS';

const addDecimalCase = (price) => {
  return price * 10;
};
const removeDecimalCase = (price) => {
  return price / 10;
};

export default {
  name: 'SellingStrategy',
  components: {
    WizardBase,
    AssessmentValueBase,
    HdRange,
    HdIcon,
  },
  data() {
    return {
      // We start from optimal price
      rangeInputValue: addDecimalCase(DEFAULT_OPTIMAL_PRICE_VALUE),
      rangeInputPrice: addDecimalCase(DEFAULT_OPTIMAL_PRICE_VALUE),
      totalPrice: this.$store.getters[GETTER.WIZARD.CURRENT_DEAL.TOTAL_PRICE],
      rangePrice:
        this.$store.getters[GETTER.WIZARD.CURRENT_DEAL.TOTAL_PRICE] ||
        Math.round(
          DEFAULT_OPTIMAL_PRICE_VALUE * this.$store.getters[GETTER.WIZARD.CURRENT_DEAL.TOTAL_PRICE],
        ),
      rangePriceIndex: FACTORS.findIndex(
        ({ price }) => addDecimalCase(price) === addDecimalCase(DEFAULT_OPTIMAL_PRICE_VALUE),
      ),
      pricePercentageDiff: '',
      skipRangeInputCalculation: false,
    };
  },
  computed: {
    marketingPrice: {
      get() {
        return this.$store.getters[GETTER.WIZARD.CURRENT_DEAL.MARKETING_PRICE];
      },
      set(value) {
        this.$store.commit(MUTATION.WIZARD.CURRENT_DEAL.SET_MARKETING_PRICE, value);
      },
    },
    customBullets() {
      return FACTORS.map(({ price }) => {
        return addDecimalCase(price);
      });
    },
    rangeLabels() {
      const defaultOptions = this.customBullets.map((price) => {
        if (price === addDecimalCase(1)) {
          return `<span class="selling-strategy__total-price">${
            this.formattedTotalPrice
          }</span>${this.$t('WIZARD.SELLING_STRATEGY.OPTIMAL_STARTING_PRICE')}`;
        }
        return '';
      });
      return defaultOptions;
    },
    min() {
      return this.customBullets[0];
    },
    max() {
      return this.customBullets[this.customBullets.length - 1];
    },
    formattedRangePrice() {
      return this.$formatPrice(this.marketingPrice, { fractionDigits: 0 });
    },
    formattedTotalPrice() {
      return this.$formatPrice(this.totalPrice, {
        fractionDigits: 0,
      });
    },
    shouldShowPriceReductionHint() {
      return CONFIG.feature_flags.edm_questionnaire;
    },
    icons() {
      return { documentsHandover, info };
    },
    priceReductionHint() {
      return this.$t('WIZARD.SELLING_STRATEGY.GENERIC_PRICE_REDUCTION_HINT');
    },
  },
  created() {
    this.updateRangePrice();
    this.updateMarketingPrice();
    this.updateRangeInputValue();
    this.updatePercentageDiff();
    this.$root.$on('marketingPriceChange', this.handleManualMarketingPriceChange);
  },
  beforeDestroy() {
    this.$root.$off('marketingPriceChange', this.handleManualMarketingPriceChange);
  },
  methods: {
    updatePriceFields() {
      if (this.skipRangeInputCalculation) return;
      this.updateRangePriceIndex();
      this.updateRangePrice();
      this.updateMarketingPrice();
      this.updateRangeInputValue();
      this.updatePercentageDiff();
    },
    updateRangePriceIndex() {
      this.rangePriceIndex = FACTORS.findIndex(
        ({ price }) => addDecimalCase(price) === this.rangeInputPrice,
      );
    },
    updateRangePrice() {
      this.rangePrice = Math.round(removeDecimalCase(this.rangeInputPrice) * this.totalPrice);
    },
    updateMarketingPrice() {
      this.marketingPrice = this.rangePrice;
    },
    updateRangeInputValue() {
      this.rangeInputValue = addDecimalCase(this.marketingPrice / this.totalPrice);
    },
    updatePercentageDiff() {
      const diff = (this.marketingPrice / this.totalPrice) * 100 - 100;
      if (!diff) this.pricePercentageDiff = `+/-0`;
      else this.pricePercentageDiff = `${diff.toFixed(0)}%`;
    },
    handleManualMarketingPriceChange(newValue) {
      this.marketingPrice = newValue;
      this.skipRangeInputCalculation = true;
      this.updateRangeInputValue();
      if (this.rangeInputValue > this.max) this.rangeInputPrice = this.max;
      if (this.rangeInputValue < this.min) this.rangeInputPrice = this.min;
      if (this.rangeInputValue > this.min && this.rangeInputValue < this.max)
        this.rangeInputPrice = this.rangeInputValue;
      this.updatePercentageDiff();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.selling-strategy {
  &__items {
    margin-bottom: $sp-l;

    @include for('tablet') {
      margin-bottom: $sp-xl;
    }

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease-in-out 0s;
    }

    &::before {
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: white;
      transition-delay: 0.3s;
    }

    &::after {
      $size: 30px;

      top: calc(50% - calc(#{$size} / 2));
      right: calc(50% - calc(#{$size} / 2));
      width: $size;
      height: $size;
      border: 2px solid getShade($secondary-color, 110);
      border-right-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    &--loading {
      &::before,
      &::after {
        transition-delay: 0s;
        opacity: 1;
      }
    }
  }

  &-item {
    display: flex;
    flex: 1;

    @include for('tablet') {
      align-items: center;
    }

    @include for('desktop') {
      flex-direction: column;
      text-align: center;
    }

    &__icon {
      flex-shrink: 0;
      width: 80px;
      height: 80px;
      background-size: 100%;
      transition: background-image 0.3s;

      @include for('tablet') {
        width: 120px;
        height: 120px;
      }
    }

    &__content {
      margin-left: $sp-m;

      @include for('desktop') {
        margin-left: 0;
      }
    }

    &__title {
      @include font('DS-200');
      font-weight: 700;

      @include for('tablet') {
        @include font('DS-400');
      }
    }

    &__description {
      word-break: break-word;
      hyphens: manual;
      @include font('DS-80');

      @include for('tablet') {
        @include font('DS-100');
      }
    }
  }

  &__footer {
    display: flex;
    align-items: flex-end;
  }

  &__range {
    margin-right: $sp-l;
    margin-left: $sp-l;
    flex: 4;

    ::v-deep .range__tooltip {
      font-weight: 600;
    }
  }

  &__label {
    flex: 1;
    @include font('DS-80');
    font-weight: 600;
    margin-bottom: $sp-s;

    &--left {
      text-align: right;
    }

    @include for('mobile-only') {
      display: none;
    }
  }

  &__lending-value {
    max-width: 700px;
    margin: $sp-m auto 0;
    padding: $sp-m;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: getShade($secondary-color, 70);
    border-radius: 8px;

    .lending-value__icon {
      min-width: 40px;
    }

    .lending-value__message {
      margin-left: $sp-s;
      font-weight: 600;
    }

    .lending-value__price {
      @include font('DS-200');
      font-weight: 700;
    }
  }

  ::v-deep &__total-price {
    display: flex;
    flex-direction: column;
    font-weight: 700;
  }

  &__price-reduction-hint {
    max-width: 700px;
    margin: $sp-xl auto 0;
    padding: $sp-l;
    display: flex;
    background-color: getShade($secondary-color, 70);
    border-radius: 8px;

    .price-reduction-hint__icon {
      margin-right: $sp-m;
      flex-shrink: 0;
    }
  }
}
</style>
